import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eraser,
  Hand,
  ImagePlus,
  Loader2,
  MinusCircle,
  PenLine,
  RotateCcw,
  Send,
  Trash2,
  Undo2,
  X,
  XCircle,
} from "lucide-react";
import { ApiService } from "../../../../services/api";

/**
 * Çalışma kâğıdı (WORKSHEET içerik).
 *
 * Öğrenci her sayfayı üç yoldan gönderir:
 *  - "Sayfaya yaz": sayfanın üstüne kalemle (tablet kalemi, parmak ya da fare) yazar. Yalnız ÇİZİM katmanı (şeffaf PNG)
 *    gönderilir, sunucu sayfa görseliyle birleştirir — sayfa görseli başka kökenden geldiği için tuvale çizilse tuval kirlenir.
 *  - "Fotoğraf çek": kâğıda çözer, telefonla fotoğrafını çeker.
 *  - "Görsel yükle": kendi çizdiği/hazırladığı görseli yükler.
 * AI cevap anahtarıyla etkinlik etkinlik puanlar; sonuç kartı puanı, doğru cevabı ve geri bildirimi gösterir.
 */

type Durum = "dogru" | "kismen" | "yanlis" | "bos";

interface EtkinlikSonucu {
  no: number;
  max: number;
  puan: number;
  durum: Durum;
  ogrenciCevabi?: string;
  dogruCevap?: string;
  geriBildirim?: string;
}

interface Gonderim {
  id: string;
  pageNo: number;
  source: "yazim" | "foto" | "gorsel";
  status: "PENDING" | "GRADED" | "FAILED";
  score: number | null;
  maxScore: number | null;
  imageUrls: string[];
  feedback: { okunabilirlik?: string; genelGeriBildirim?: string; etkinlikler?: EtkinlikSonucu[] } | null;
  createdAt: string;
}

interface Sayfa {
  no: number;
  kitapSayfa: number | null;
  imageUrl: string;
  width: number | null;
  height: number | null;
  etkinlikler: number[];
  maxScore: number;
  denemeSayisi: number;
  sonGonderim: Gonderim | null;
}

interface CalismaKagidi {
  id: string;
  title: string;
  aciklama: string | null;
  maddeEtiketi?: string;
  pages: Sayfa[];
  ozet: { sayfa: number; puanlanan: number; puan: number; max: number };
}

const DURUM: Record<Durum, { label: string; className: string; icon: JSX.Element }> = {
  dogru: { label: "Doğru", className: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <CheckCircle2 className="w-4 h-4" /> },
  kismen: { label: "Kısmen", className: "bg-amber-50 text-amber-700 border-amber-200", icon: <MinusCircle className="w-4 h-4" /> },
  yanlis: { label: "Yanlış", className: "bg-red-50 text-red-700 border-red-200", icon: <XCircle className="w-4 h-4" /> },
  bos: { label: "Boş", className: "bg-slate-50 text-slate-600 border-slate-200", icon: <MinusCircle className="w-4 h-4" /> },
};

const formatPuan = (n: number | null | undefined) => (Number(n) || 0).toLocaleString("tr-TR", { maximumFractionDigits: 1 });

export default function WorksheetViewer({ content }: { content: { id: string; title?: string } }) {
  const [data, setData] = useState<CalismaKagidi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [annotating, setAnnotating] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileSource, setFileSource] = useState<"foto" | "gorsel">("foto");
  const [answerText, setAnswerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [retryPayload, setRetryPayload] = useState<{ source: "yazim" | "foto" | "gorsel"; images: Blob[] } | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews]);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      setError("");
      const res = await ApiService.getWorksheet(content.id);
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Çalışma kâğıdı yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [content.id]);

  useEffect(() => {
    setLoading(true);
    setPageIndex(0);
    load();
  }, [load]);

  const page = data?.pages[pageIndex] || null;
  const last = page?.sonGonderim || null;

  const resetDraft = () => {
    setFiles([]);
    setAnswerText("");
    setSubmitError("");
    setRetryPayload(null);
  };

  const goTo = (index: number) => {
    if (!data) return;
    setPageIndex(Math.max(0, Math.min(data.pages.length - 1, index)));
    resetDraft();
  };

  const submit = async (source: "yazim" | "foto" | "gorsel", images: Blob[]) => {
    if (!page || !data) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const result: Gonderim = await ApiService.submitWorksheetPage(data.id, page.no, { source, images, answerText: answerText.trim() || undefined });
      if (result?.status === "FAILED") {
        setRetryPayload({ source, images });
        setSubmitError("Çalışman şu an puanlanamadı. Birazdan tekrar gönder.");
      } else {
        setFiles([]);
        setAnswerText("");
        setRetryPayload(null);
      }
      await load();
    } catch (e: any) {
      setRetryPayload({ source, images });
      setSubmitError(e?.message || "Gönderilemedi. İnternet bağlantını kontrol edip tekrar dene.");
    } finally {
      setSubmitting(false);
    }
  };

  const onFiles = (list: FileList | null, source: "foto" | "gorsel") => {
    if (!list?.length) return;
    const picked = Array.from(list).filter((f) => f.type.startsWith("image/"));
    setFileSource(source);
    setFiles((prev) => [...prev, ...picked].slice(0, 4));
    setSubmitError("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Çalışma kâğıdı yükleniyor…
      </div>
    );
  }
  if (error || !data || !page) {
    return (
      <div className="max-w-xl mx-auto my-12 p-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm flex gap-2">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <span>{error || "Çalışma kâğıdı bulunamadı."}</span>
      </div>
    );
  }

  return (
    <div className="w-full bg-slate-50">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">
        {/* Özet */}
        <div className="rounded-xl bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-600">{data.aciklama}</div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div>
              <div className="text-slate-500 text-xs">Puanlanan sayfa</div>
              <div className="font-semibold text-slate-800 tabular-nums">{data.ozet.puanlanan} / {data.ozet.sayfa}</div>
            </div>
            <div>
              <div className="text-slate-500 text-xs">Toplam puan</div>
              <div className="font-semibold text-slate-800 tabular-nums">{formatPuan(data.ozet.puan)} / {formatPuan(data.ozet.max)}</div>
            </div>
          </div>
        </div>

        {/* Sayfa seçici */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goTo(pageIndex - 1)}
            disabled={pageIndex === 0}
            className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40"
            aria-label="Önceki sayfa">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 overflow-x-auto">
            <div className="flex gap-1.5 w-max">
              {data.pages.map((p, i) => {
                const s = p.sonGonderim;
                const tone =
                  s?.status === "GRADED"
                    ? (s.score || 0) >= p.maxScore * 0.7
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : "border-amber-300 bg-amber-50 text-amber-700"
                    : s?.status === "FAILED"
                      ? "border-red-300 bg-red-50 text-red-700"
                      : "border-slate-200 bg-white text-slate-600";
                return (
                  <button
                    key={p.no}
                    type="button"
                    onClick={() => goTo(i)}
                    className={`min-w-[2.75rem] px-2 py-1.5 rounded-lg border text-xs font-medium tabular-nums ${tone} ${i === pageIndex ? "ring-2 ring-blue-500" : ""}`}>
                    <div>{p.no}. sayfa</div>
                    {s?.status === "GRADED" && <div className="text-[11px]">{formatPuan(s.score)}/{p.maxScore}</div>}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => goTo(pageIndex + 1)}
            disabled={pageIndex === data.pages.length - 1}
            className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40"
            aria-label="Sonraki sayfa">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          {/* Sayfa */}
          <div className="min-w-0 rounded-xl bg-white border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 text-xs text-slate-500 flex justify-between">
              <span>{data.maddeEtiketi || "Etkinlik"} {page.etkinlikler.join(", ")}</span>
              {page.kitapSayfa && <span>Kitap s.{page.kitapSayfa}</span>}
            </div>
            <img src={page.imageUrl} alt={`Çalışma kâğıdı ${page.no}. sayfa`} className="block w-full h-auto" />
          </div>

          {/* Gönderim + sonuç */}
          <div className="min-w-0 space-y-3">
            <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-3">
              <div className="text-sm font-semibold text-slate-800">{last ? "Yeniden çöz" : "Çözümünü gönder"}</div>
              <button
                type="button"
                onClick={() => setAnnotating(true)}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2.5 hover:bg-blue-700 disabled:opacity-50">
                <PenLine className="w-4 h-4" /> Sayfanın üstüne yaz
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={submitting}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm px-3 py-2 hover:bg-slate-50 disabled:opacity-50">
                  <Camera className="w-4 h-4" /> Fotoğraf çek
                </button>
                <button
                  type="button"
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={submitting}
                  className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm px-3 py-2 hover:bg-slate-50 disabled:opacity-50">
                  <ImagePlus className="w-4 h-4" /> Görsel yükle
                </button>
              </div>
              <input ref={cameraInputRef} id="worksheet-camera" type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => { onFiles(e.target.files, "foto"); e.target.value = ""; }} />
              <input ref={uploadInputRef} id="worksheet-upload" type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => { onFiles(e.target.files, "gorsel"); e.target.value = ""; }} />

              {files.length > 0 && (
                <div className="space-y-2">
                  <div className="grid grid-cols-4 gap-2">
                    {files.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="relative aspect-square rounded-md overflow-hidden border border-slate-200">
                        <img src={previews[i]} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                          className="absolute top-0.5 right-0.5 p-0.5 rounded bg-white/90 text-slate-700" aria-label="Görseli kaldır">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <textarea
                    id="worksheet-answer-text"
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    rows={2}
                    placeholder="İstersen kısa bir not ekle (ör. hangi etkinliği çözdüğün)"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  />
                  <button
                    type="button"
                    onClick={() => submit(fileSource, files)}
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2.5 hover:bg-blue-700 disabled:opacity-50">
                    <Send className="w-4 h-4" /> Gönder ve puanla
                  </button>
                </div>
              )}

              {submitting && (
                <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Çalışman değerlendiriliyor (10–20 sn)…
                </div>
              )}
              {submitError && (
                <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2 space-y-2">
                  <div>{submitError}</div>
                  {retryPayload && (
                    <button type="button" onClick={() => submit(retryPayload.source, retryPayload.images)} disabled={submitting}
                      className="inline-flex items-center gap-1.5 text-xs font-medium underline">
                      <RotateCcw className="w-3.5 h-3.5" /> Tekrar gönder
                    </button>
                  )}
                </div>
              )}
            </div>

            {last && <SonucKarti gonderim={last} sayfa={page} etiket={data.maddeEtiketi || "Etkinlik"} />}
          </div>
        </div>
      </div>

      {annotating &&
        createPortal(
          <PageAnnotator
            imageUrl={page.imageUrl}
            width={page.width}
            height={page.height}
            title={`${data.title} · ${page.no}. sayfa`}
            onClose={() => setAnnotating(false)}
            onSubmit={async (blob) => {
              setAnnotating(false);
              await submit("yazim", [blob]);
            }}
          />,
          document.body
        )}
    </div>
  );
}

function SonucKarti({ gonderim, sayfa, etiket }: { gonderim: Gonderim; sayfa: Sayfa; etiket: string }) {
  if (gonderim.status === "FAILED") {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Son gönderimin puanlanamadı. Çalışmanı yeniden gönderebilirsin.
      </div>
    );
  }
  if (gonderim.status !== "GRADED") return null;
  const fb = gonderim.feedback || {};
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-semibold text-slate-800">Son değerlendirme</div>
        <div className="text-lg font-semibold text-slate-900 tabular-nums">
          {formatPuan(gonderim.score)} <span className="text-sm font-normal text-slate-500">/ {formatPuan(gonderim.maxScore ?? sayfa.maxScore)}</span>
        </div>
      </div>
      {fb.okunabilirlik === "okunamadi" && (
        <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-2.5 py-1.5">
          Çalışmanın bir kısmı okunamadı. Daha net bir fotoğrafla tekrar gönderebilirsin.
        </div>
      )}
      {fb.genelGeriBildirim && <p className="text-sm text-slate-700">{fb.genelGeriBildirim}</p>}
      <div className="space-y-2">
        {(fb.etkinlikler || []).map((e) => {
          const d = DURUM[e.durum] || DURUM.bos;
          return (
            <div key={e.no} className="rounded-lg border border-slate-200 p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-800">{etiket} {e.no}</span>
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${d.className}`}>
                  {d.icon} {d.label} · {formatPuan(e.puan)}/{e.max}
                </span>
              </div>
              {(e.ogrenciCevabi || e.dogruCevap) && (
                <div className="text-xs text-slate-600 grid grid-cols-2 gap-2">
                  <div><span className="text-slate-400">Senin cevabın: </span>{e.ogrenciCevabi || "—"}</div>
                  <div><span className="text-slate-400">Doğru cevap: </span>{e.dogruCevap || "—"}</div>
                </div>
              )}
              {e.geriBildirim && <p className="text-sm text-slate-700">{e.geriBildirim}</p>}
            </div>
          );
        })}
      </div>
      {gonderim.imageUrls?.[0] && (
        <a href={gonderim.imageUrls[0]} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 hover:underline">
          Gönderdiğin çalışmayı aç
        </a>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sayfanın üstüne yazma katmanı                                       */
/* ------------------------------------------------------------------ */

const KALEM_RENKLERI = ["#1d4ed8", "#111827", "#dc2626", "#059669"];

interface Cizgi {
  silgi: boolean;
  renk: string;
  kalinlik: number; // tuval pikseli
  noktalar: Array<[number, number]>;
}

function PageAnnotator({
  imageUrl,
  width,
  height,
  title,
  onClose,
  onSubmit,
}: {
  imageUrl: string;
  width: number | null;
  height: number | null;
  title: string;
  onClose: () => void;
  onSubmit: (blob: Blob) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const cizgilerRef = useRef<Cizgi[]>([]);
  const aktifRef = useRef<{ pointerId: number; cizgi: Cizgi } | null>(null);
  const kalemGorulduRef = useRef(false);
  const [boyut, setBoyut] = useState<{ w: number; h: number } | null>(width && height ? { w: width, h: height } : null);
  const [arac, setArac] = useState<"kalem" | "silgi" | "kaydir">("kalem");
  const [renk, setRenk] = useState(KALEM_RENKLERI[0]);
  const [kalinlik, setKalinlik] = useState(3);
  const [cizgiSayisi, setCizgiSayisi] = useState(0);
  const [onayKapat, setOnayKapat] = useState(false);

  // Görselin gerçek boyutu bilinmiyorsa yüklenince öğrenilir; tuval sayfanın PİKSEL boyutundadır.
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!boyut) setBoyut({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
  };

  const yenidenCiz = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const c of cizgilerRef.current) {
      ctx.globalCompositeOperation = c.silgi ? "destination-out" : "source-over";
      ctx.strokeStyle = c.renk;
      ctx.lineWidth = c.kalinlik;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      c.noktalar.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      if (c.noktalar.length === 1) ctx.lineTo(c.noktalar[0][0] + 0.1, c.noktalar[0][1]);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = "source-over";
  }, []);

  useEffect(() => {
    if (boyut) yenidenCiz();
  }, [boyut, yenidenCiz]);

  const nokta = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return [((e.clientX - rect.left) * canvas.width) / rect.width, ((e.clientY - rect.top) * canvas.height) / rect.height];
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (arac === "kaydir") return;
    if (e.pointerType === "pen") kalemGorulduRef.current = true;
    // Avuç teması: kalem bir kez görüldüyse parmak dokunuşu çizim yapmaz.
    if (e.pointerType === "touch" && kalemGorulduRef.current) return;
    if (aktifRef.current) return;
    const canvas = canvasRef.current!;
    const olcek = canvas.width / canvas.getBoundingClientRect().width;
    const cizgi: Cizgi = {
      silgi: arac === "silgi",
      renk,
      kalinlik: (arac === "silgi" ? 22 : kalinlik) * olcek,
      noktalar: [nokta(e)],
    };
    aktifRef.current = { pointerId: e.pointerId, cizgi };
    cizgilerRef.current.push(cizgi);
    canvas.setPointerCapture(e.pointerId);
    yenidenCiz();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const aktif = aktifRef.current;
    if (!aktif || aktif.pointerId !== e.pointerId) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const [x, y] = nokta(e);
    const [px, py] = aktif.cizgi.noktalar[aktif.cizgi.noktalar.length - 1];
    aktif.cizgi.noktalar.push([x, y]);
    ctx.globalCompositeOperation = aktif.cizgi.silgi ? "destination-out" : "source-over";
    ctx.strokeStyle = aktif.cizgi.renk;
    ctx.lineWidth = aktif.cizgi.kalinlik;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (aktifRef.current?.pointerId !== e.pointerId) return;
    aktifRef.current = null;
    setCizgiSayisi(cizgilerRef.current.filter((c) => !c.silgi).length);
  };

  const geriAl = () => {
    cizgilerRef.current.pop();
    setCizgiSayisi(cizgilerRef.current.filter((c) => !c.silgi).length);
    yenidenCiz();
  };

  const temizle = () => {
    cizgilerRef.current = [];
    setCizgiSayisi(0);
    yenidenCiz();
  };

  const gonder = () => {
    canvasRef.current?.toBlob((blob) => {
      if (blob) onSubmit(blob);
    }, "image/png");
  };

  const kapat = () => {
    if (cizgiSayisi > 0 && !onayKapat) {
      setOnayKapat(true);
      return;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-900/95 flex flex-col" style={{ height: "100dvh" }}>
      {/* Araç çubuğu */}
      <div className="shrink-0 bg-white border-b border-slate-200 px-2 sm:px-4 py-2 flex flex-wrap items-center gap-2">
        <button type="button" onClick={kapat} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Kapat">
          <X className="w-5 h-5" />
        </button>
        <div className="text-sm font-medium text-slate-800 truncate max-w-[40vw] hidden sm:block">{title}</div>
        <div className="flex items-center gap-1 ml-auto">
          {(
            [
              ["kalem", <PenLine key="k" className="w-4 h-4" />, "Kalem"],
              ["silgi", <Eraser key="s" className="w-4 h-4" />, "Silgi"],
              ["kaydir", <Hand key="h" className="w-4 h-4" />, "Kaydır"],
            ] as const
          ).map(([deger, ikon, ad]) => (
            <button
              key={deger}
              type="button"
              onClick={() => setArac(deger)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border ${arac === deger ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200"}`}>
              {ikon} <span className="hidden sm:inline">{ad}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {KALEM_RENKLERI.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => { setRenk(r); setArac("kalem"); }}
              className={`w-6 h-6 rounded-full border-2 ${renk === r && arac === "kalem" ? "border-slate-900" : "border-white shadow"}`}
              style={{ backgroundColor: r }}
              aria-label="Kalem rengi"
            />
          ))}
          <select
            id="worksheet-pen-size"
            value={kalinlik}
            onChange={(e) => setKalinlik(Number(e.target.value))}
            className="ml-1 text-xs border border-slate-200 rounded-md px-1.5 py-1"
            aria-label="Kalem kalınlığı">
            <option value={2}>İnce</option>
            <option value={3}>Orta</option>
            <option value={5}>Kalın</option>
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={geriAl} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Geri al">
            <Undo2 className="w-4 h-4" />
          </button>
          <button type="button" onClick={temizle} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Hepsini sil">
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={gonder}
            disabled={cizgiSayisi === 0}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium px-3 py-1.5 disabled:opacity-40">
            <Send className="w-4 h-4" /> Gönder
          </button>
        </div>
      </div>

      {onayKapat && (
        <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800 flex flex-wrap items-center gap-3">
          Yazdıkların gönderilmedi ve silinecek.
          <button type="button" onClick={onClose} className="font-medium underline">Yine de kapat</button>
          <button type="button" onClick={() => setOnayKapat(false)} className="font-medium underline">Yazmaya devam et</button>
        </div>
      )}

      {/* Sayfa + çizim katmanı */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="mx-auto my-3 max-w-3xl px-2">
          <div className="relative bg-white shadow-lg">
            <img src={imageUrl} alt="" onLoad={onImageLoad} className="block w-full h-auto select-none pointer-events-none" draggable={false} />
            {boyut && (
              <canvas
                ref={canvasRef}
                width={boyut.w}
                height={boyut.h}
                className="absolute inset-0 w-full h-full"
                style={{ touchAction: arac === "kaydir" ? "auto" : "none", pointerEvents: arac === "kaydir" ? "none" : "auto", cursor: arac === "kaydir" ? "default" : "crosshair" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={onPointerUp}
              />
            )}
          </div>
          <p className="text-center text-xs text-slate-300 my-3">
            Sayfayı kaydırmak için "Kaydır"a geç. Tablet kalemi kullanıyorsan elin ekrana değse de çizmez.
          </p>
        </div>
      </div>
    </div>
  );
}
